(() => {
  'use strict';

  const theme = window.WWTheme || (window.WWTheme = {});

  if (theme.runtime && typeof theme.runtime.init === 'function') {
    theme.runtime.init(document);
    return;
  }

  const VERSION = '1.1.1';
  const FOCUSABLE = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'details > summary:first-of-type',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const REVEAL_SELECTOR = '[data-reveal], [data-ww-reveal], .ww-reveal, .reveal';
  const GALLERY_SELECTOR = '[data-product-gallery], [data-gallery]';
  const GALLERY_ITEM_SELECTOR = '[data-gallery-item], [data-product-media]';
  const GALLERY_CONTROL_SELECTOR = '[data-gallery-thumbnail], [data-gallery-thumb], [data-media-target]';
  const PRODUCT_ROOT_SELECTOR = '[data-product-root], [data-product-section], [data-product]';

  const reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
  const footerBreakpoint = window.matchMedia
    ? window.matchMedia('(max-width: 767px)')
    : { matches: false };

  let booted = false;
  let uniqueId = 0;
  let activeSurface = null;
  let activeIntro = null;
  let headerFrame = 0;
  let lastScrollY = window.scrollY || 0;
  let bodyLock = null;
  let revealObserver = null;
  let cartRequestPending = false;

  const introTimers = new WeakMap();
  const introStageTimers = new WeakMap();
  const closeTimers = new WeakMap();
  const pendingForms = new WeakSet();
  const stickyRecords = new Set();

  function elementsWithin(root, selector) {
    const elements = [];
    if (root && root.nodeType === 1 && root.matches(selector)) elements.push(root);
    if (root && root.querySelectorAll) elements.push(...root.querySelectorAll(selector));
    return elements;
  }

  function parseBoolean(value, fallback = false) {
    if (value == null || value === '') return fallback;
    return !['false', '0', 'off', 'no'].includes(String(value).toLowerCase());
  }

  function isDesignMode() {
    return Boolean(theme.designMode || (window.Shopify && window.Shopify.designMode));
  }

  function isMotionReduced() {
    return reducedMotion.matches || document.documentElement.classList.contains('motion-none');
  }

  function currentMotionLevel() {
    if (document.documentElement.classList.contains('motion-none')) return 'none';
    if (document.documentElement.classList.contains('motion-full')) return 'full';
    return 'restrained';
  }

  function syncMotionControls(root = document) {
    const level = currentMotionLevel();
    elementsWithin(root, '[data-motion-control]').forEach((control) => {
      const toggle = control.querySelector('[data-motion-toggle]');
      if (toggle) toggle.setAttribute('aria-label', `Motion preference: ${level}. Change motion preference.`);
      control.querySelectorAll('[data-motion-value]').forEach((button) => {
        const active = button.dataset.motionValue === level;
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    });
  }

  function closeMotionMenus(except = null) {
    document.querySelectorAll('[data-motion-control]').forEach((control) => {
      if (control === except) return;
      const menu = control.querySelector('[data-motion-menu]');
      const toggle = control.querySelector('[data-motion-toggle]');
      if (menu) menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
  }

  function applyMotionPreference(level, persist = true) {
    if (!['full', 'restrained', 'none'].includes(level)) level = theme.defaultMotion || 'restrained';
    document.documentElement.classList.remove('motion-full', 'motion-restrained', 'motion-none');
    document.documentElement.classList.add(`motion-${level}`);
    document.documentElement.dataset.motion = level;
    if (persist) {
      try {
        document.cookie = `${theme.motionCookie || 'ww_motion_bolt_111'}=${level}; Max-Age=31536000; Path=/; SameSite=Lax`;
      } catch (_) {
        // A blocked preference store never affects access to the storefront.
      }
    }
    if (level !== 'full' && activeIntro) finishIntro(activeIntro, true);
    if (level === 'none') {
      if (activeIntro) finishIntro(activeIntro, true);
      document.querySelectorAll(REVEAL_SELECTOR).forEach(revealElement);
    }
    syncMotionControls(document);
  }

  function initMotionControls(root) {
    elementsWithin(root, '[data-motion-control]').forEach((control) => {
      if (control.dataset.wwMotionReady === 'true') return;
      control.dataset.wwMotionReady = 'true';
      const menu = control.querySelector('[data-motion-menu]');
      const toggle = control.querySelector('[data-motion-toggle]');
      if (menu) menu.hidden = true;
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    });
    syncMotionControls(root);
  }

  function isVisible(element) {
    if (!element || element.hidden || element.closest('[hidden]')) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  function focusableWithin(container) {
    if (!container) return [];
    return Array.from(container.querySelectorAll(FOCUSABLE)).filter((element) => {
      return isVisible(element) && element.getAttribute('aria-hidden') !== 'true' && !element.closest('[inert]');
    });
  }

  function announce(message) {
    if (!message) return;
    const region = document.getElementById('ww-live-region') || document.querySelector('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => {
      region.textContent = String(message);
    }, 20);
  }

  function recoverProgressiveEnhancement(error) {
    try {
      if (activeIntro) finishIntro(activeIntro, true);
      if (activeSurface) closeSurface({ returnFocus: false, immediate: true });
      unlockBody();
      document.querySelectorAll(REVEAL_SELECTOR).forEach(revealElement);
    } catch (_) {
      // Recovery must never create a second storefront failure.
    }
    document.documentElement.classList.remove('js');
    document.documentElement.classList.add('no-js');
    if (error && window.console?.error) console.error('Wednesday Woe theme initialization failed.', error);
  }

  function safeInit(root = document) {
    try {
      init(root);
      return true;
    } catch (error) {
      recoverProgressiveEnhancement(error);
      return false;
    }
  }

  function lockBody() {
    if (bodyLock) return;
    const body = document.body;
    const scrollbar = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
    bodyLock = {
      overflow: body.style.overflow,
      paddingRight: body.style.paddingRight
    };
    body.style.overflow = 'hidden';
    if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
    body.classList.add('is-drawer-open');
    document.documentElement.classList.add('is-drawer-open');
  }

  function unlockBody() {
    if (!bodyLock) return;
    const body = document.body;
    body.style.overflow = bodyLock.overflow;
    body.style.paddingRight = bodyLock.paddingRight;
    body.classList.remove('is-drawer-open');
    document.documentElement.classList.remove('is-drawer-open');
    bodyLock = null;
  }

  function surfaceElements(kind) {
    if (kind === 'menu') {
      const shell = document.querySelector('[data-mobile-menu]');
      return shell ? { shell, dialog: shell.querySelector('[role="dialog"]') || shell } : null;
    }
    if (kind === 'cart') {
      const shell = document.querySelector('[data-cart-shell]');
      return shell ? { shell, dialog: shell.querySelector('[data-cart-drawer]') || shell } : null;
    }
    if (kind === 'filter') {
      const shell = document.querySelector('[data-filter-shell]') || document.querySelector('[data-filter-drawer]');
      return shell ? { shell, dialog: shell.querySelector('[data-filter-drawer]') || shell } : null;
    }
    return null;
  }

  function surfaceTriggers(kind) {
    if (kind === 'menu') return document.querySelectorAll('[data-menu-open]');
    if (kind === 'cart') return document.querySelectorAll('[data-cart-open]');
    if (kind === 'filter') return document.querySelectorAll('[data-filter-open], [data-filter-toggle]');
    return [];
  }

  function setTriggerState(kind, expanded) {
    surfaceTriggers(kind).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  function openSurface(kind, opener, options = {}) {
    const surface = surfaceElements(kind);
    if (!surface) return false;

    if (activeSurface && activeSurface.shell === surface.shell) return true;
    if (activeSurface) closeSurface({ returnFocus: false, preserveLock: true, immediate: true });

    const previousTimer = closeTimers.get(surface.shell);
    if (previousTimer) window.clearTimeout(previousTimer);

    activeSurface = {
      kind,
      shell: surface.shell,
      dialog: surface.dialog,
      opener: opener && opener.isConnected ? opener : null
    };

    if (kind === 'filter') {
      surface.dialog.setAttribute('role', 'dialog');
      surface.dialog.setAttribute('aria-modal', 'true');
      if (!surface.dialog.hasAttribute('tabindex')) surface.dialog.setAttribute('tabindex', '-1');
    }

    surface.shell.hidden = false;
    surface.shell.setAttribute('aria-hidden', 'false');
    surface.shell.classList.remove('is-closing');
    // Force a style calculation so a hidden drawer can animate on its first opening.
    void surface.shell.offsetWidth;
    surface.shell.classList.add('is-open');
    surface.dialog.classList.add('is-open');
    setTriggerState(kind, true);
    if (!options.preserveLock) lockBody();
    else lockBody();

    const header = document.querySelector('[data-ww-header]');
    if (header) header.classList.remove('is-hidden');

    window.requestAnimationFrame(() => {
      if (!activeSurface || activeSurface.shell !== surface.shell) return;
      const focusTarget = surface.dialog.querySelector('[data-autofocus]') ||
        focusableWithin(surface.dialog)[0] || surface.dialog;
      if (!focusTarget.hasAttribute('tabindex') && focusTarget === surface.dialog) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      focusTarget.focus({ preventScroll: true });
    });

    return true;
  }

  function closeSurface(options = {}) {
    if (!activeSurface) return;
    const current = activeSurface;
    activeSurface = null;

    current.shell.classList.remove('is-open');
    current.dialog.classList.remove('is-open');
    current.shell.classList.add('is-closing');
    current.shell.setAttribute('aria-hidden', 'true');
    setTriggerState(current.kind, false);

    const hide = () => {
      if (current.shell.classList.contains('is-open')) return;
      current.shell.hidden = true;
      current.shell.classList.remove('is-closing');
      closeTimers.delete(current.shell);
    };

    if (options.immediate || isMotionReduced()) hide();
    else closeTimers.set(current.shell, window.setTimeout(hide, 360));

    if (!options.preserveLock) unlockBody();

    if (options.returnFocus !== false) {
      const fallback = current.kind === 'cart'
        ? document.querySelector('[data-cart-open]')
        : current.kind === 'menu'
          ? document.querySelector('[data-menu-open]')
          : document.querySelector('[data-filter-open], [data-filter-toggle]');
      const returnTarget = current.opener && current.opener.isConnected ? current.opener : fallback;
      if (returnTarget) window.requestAnimationFrame(() => returnTarget.focus({ preventScroll: true }));
    }
  }

  function trapFocus(event) {
    if (!activeSurface || event.key !== 'Tab') return;
    const focusable = focusableWithin(activeSurface.dialog);
    if (!focusable.length) {
      event.preventDefault();
      activeSurface.dialog.focus({ preventScroll: true });
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && (document.activeElement === first || !activeSurface.dialog.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !activeSurface.dialog.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }

  function updateHeader() {
    headerFrame = 0;
    const header = document.querySelector('[data-ww-header]');
    if (!header) return;

    const currentY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    const solidThreshold = Number.parseInt(header.dataset.solidThreshold || '32', 10);
    const hideEnabled = parseBoolean(header.dataset.hideOnScroll, false);
    const forceSolid = parseBoolean(header.dataset.forceSolid, false);
    const delta = currentY - lastScrollY;
    const headerHasFocus = header.contains(document.activeElement);

    header.classList.toggle('is-solid', forceSolid || currentY > solidThreshold);
    header.classList.toggle('is-scrolled', currentY > solidThreshold);

    if (!activeSurface && !headerHasFocus && hideEnabled && currentY > 120 && delta > 3) {
      header.classList.add('is-hidden');
    } else if (delta < -3 || currentY <= 120 || activeSurface || headerHasFocus) {
      header.classList.remove('is-hidden');
    }

    lastScrollY = currentY;
  }

  function scheduleHeaderUpdate() {
    if (headerFrame) return;
    headerFrame = window.requestAnimationFrame(updateHeader);
  }

  function syncFooterDisclosure(button) {
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!panel) return;

    if (!button.dataset.mobileExpanded) {
      button.dataset.mobileExpanded = button.getAttribute('aria-expanded') === 'true' ? 'true' : 'false';
    }

    const expanded = footerBreakpoint.matches
      ? button.dataset.mobileExpanded === 'true'
      : true;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.hidden = !expanded;
    button.closest('.ww-footer__group')?.classList.toggle('is-open', expanded);
  }

  function initFooterDisclosures(root) {
    elementsWithin(root, '[data-footer-toggle]').forEach((button) => {
      const panelId = button.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : button.nextElementSibling;
      if (!panel) return;
      if (!panel.id) panel.id = `FooterDisclosure-${++uniqueId}`;
      button.setAttribute('aria-controls', panel.id);
      syncFooterDisclosure(button);
    });
  }

  function syncAllFooterDisclosures() {
    document.querySelectorAll('[data-footer-toggle]').forEach(syncFooterDisclosure);
  }

  function syncFilterDrawers(root = document) {
    elementsWithin(root, '[data-filter-drawer]').forEach((drawer) => {
      const isActive = activeSurface?.kind === 'filter' && activeSurface.shell === drawer;
      if (footerBreakpoint.matches) {
        drawer.setAttribute('role', 'dialog');
        drawer.setAttribute('aria-modal', 'true');
        if (!drawer.hasAttribute('aria-label') && !drawer.hasAttribute('aria-labelledby')) {
          drawer.setAttribute('aria-label', 'Product filters');
        }
        if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');
        if (!isActive) {
          drawer.hidden = true;
          drawer.setAttribute('aria-hidden', 'true');
          drawer.classList.remove('is-open');
          setTriggerState('filter', false);
        }
      } else {
        if (isActive) closeSurface({ returnFocus: false, immediate: true });
        drawer.hidden = false;
        drawer.removeAttribute('aria-hidden');
        drawer.removeAttribute('aria-modal');
        drawer.removeAttribute('role');
        drawer.classList.remove('is-open', 'is-closing');
        setTriggerState('filter', false);
      }
    });
  }

  function syncNativeDisclosure(details) {
    const summary = details.querySelector(':scope > summary') || details.querySelector('[data-disclosure-button]');
    if (summary) {
      let panel = null;
      const controlledId = summary.getAttribute('aria-controls');
      if (controlledId) panel = document.getElementById(controlledId);
      panel = panel || details.querySelector('[data-disclosure-panel], .letter');
      if (panel && !panel.id) panel.id = `DisclosurePanel-${++uniqueId}`;
      if (panel) summary.setAttribute('aria-controls', panel.id);
      summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
      summary.querySelectorAll('[data-disclosure-open-label], .envelope__btn-open').forEach((label) => {
        label.hidden = details.open;
      });
      summary.querySelectorAll('[data-disclosure-close-label], .envelope__btn-close').forEach((label) => {
        label.hidden = !details.open;
      });
    }
    details.classList.toggle('is-open', details.open);
  }

  function initDisclosures(root) {
    elementsWithin(root, '[data-disclosure]').forEach((disclosure) => {
      if (disclosure.tagName === 'DETAILS') {
        syncNativeDisclosure(disclosure);
        disclosure.dataset.wwDisclosureReady = 'true';
        return;
      }

      const button = disclosure.querySelector('[data-disclosure-button], [aria-controls]');
      if (!button) return;
      let panel = null;
      const controlledId = button.getAttribute('aria-controls');
      if (controlledId) panel = document.getElementById(controlledId);
      panel = panel || disclosure.querySelector('[data-disclosure-panel]');
      if (!panel) return;
      if (!panel.id) panel.id = `DisclosurePanel-${++uniqueId}`;
      button.setAttribute('aria-controls', panel.id);
      const expanded = button.getAttribute('aria-expanded') === 'true' || disclosure.classList.contains('is-open');
      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      panel.hidden = !expanded;
      disclosure.classList.toggle('is-open', expanded);
      disclosure.dataset.wwDisclosureReady = 'true';
    });
  }

  function toggleCustomDisclosure(button) {
    const disclosure = button.closest('[data-disclosure]');
    if (!disclosure || disclosure.tagName === 'DETAILS') return;
    const panelId = button.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : disclosure.querySelector('[data-disclosure-panel]');
    if (!panel) return;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    panel.hidden = expanded;
    disclosure.classList.toggle('is-open', !expanded);
  }

  function finishIntro(intro) {
    if (activeIntro === intro) activeIntro = null;
    window.WWIntro?.finish();
  }

  function initIntros(root) {
    elementsWithin(root, '[data-ww-intro]').forEach(intro => {
      window.WWIntro?.mount(intro);
      if (window.WWIntro?.active === intro) activeIntro = intro;
    });
  }

  function revealElement(element) {
    element.classList.add('is-in', 'is-revealed', 'is-visible');
    element.dataset.revealed = 'true';
    element.removeAttribute('aria-hidden');
    if (revealObserver) revealObserver.unobserve(element);
  }

  function getRevealObserver() {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) revealElement(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    return revealObserver;
  }

  function initReveals(root) {
    elementsWithin(root, REVEAL_SELECTOR).forEach((element) => {
      if (element.dataset.wwRevealReady === 'true') return;
      element.dataset.wwRevealReady = 'true';
      if (isDesignMode() || isMotionReduced() || !('IntersectionObserver' in window)) {
        revealElement(element);
      } else {
        getRevealObserver()?.observe(element);
      }
    });
  }

  function updateCategoryRail(rail) {
    const cards = Array.from(rail.querySelectorAll('[data-category-card]'));
    const dots = Array.from(rail.parentElement?.querySelectorAll('[data-category-dot]') || []);
    if (!cards.length || !dots.length) return;
    const activeIndex = cards.reduce((nearest, card, index) => {
      const currentDistance = Math.abs(card.offsetLeft - rail.scrollLeft);
      const nearestDistance = Math.abs(cards[nearest].offsetLeft - rail.scrollLeft);
      return currentDistance < nearestDistance ? index : nearest;
    }, 0);
    dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
      dot.setAttribute('aria-current', index === activeIndex ? 'true' : 'false');
    });
  }

  function initCategoryRails(root) {
    elementsWithin(root, '[data-category-rail]').forEach((rail) => {
      if (rail.dataset.wwRailReady === 'true') return;
      rail.dataset.wwRailReady = 'true';
      let frame = 0;
      rail.addEventListener('scroll', () => {
        if (frame) return;
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          updateCategoryRail(rail);
        });
      }, { passive: true });
      updateCategoryRail(rail);
    });
  }

  function galleryControls(gallery) {
    return Array.from(gallery.querySelectorAll(GALLERY_CONTROL_SELECTOR));
  }

  function galleryItems(gallery) {
    return Array.from(gallery.querySelectorAll(GALLERY_ITEM_SELECTOR));
  }

  function galleryKeyForControl(control) {
    return control.getAttribute('aria-controls') ||
      control.dataset.mediaId ||
      control.dataset.galleryThumbnail ||
      control.dataset.galleryThumb ||
      control.dataset.mediaTarget ||
      control.dataset.galleryTarget ||
      control.dataset.index || '';
  }

  function findGalleryItem(items, key) {
    if (!items.length) return null;
    if (key && document.getElementById(key) && items.includes(document.getElementById(key))) {
      return document.getElementById(key);
    }
    const asIndex = Number.parseInt(key, 10);
    return items.find((item, index) => {
      return item.id === key || item.dataset.mediaId === key || item.dataset.galleryItem === key ||
        (Number.isFinite(asIndex) && index === asIndex);
    }) || null;
  }

  function activateGallery(gallery, keyOrControl, focusControl = false, scrollMedia = false) {
    if (!gallery) return;
    const items = galleryItems(gallery);
    const controls = galleryControls(gallery);
    if (!items.length) return;

    const key = keyOrControl && keyOrControl.nodeType === 1
      ? galleryKeyForControl(keyOrControl)
      : String(keyOrControl == null ? '' : keyOrControl);
    const target = findGalleryItem(items, key) || items.find((item) => item.classList.contains('is-active')) || items[0];
    const targetIndex = items.indexOf(target);
    const showAll = ['grid', 'slider'].includes(gallery.dataset.galleryLayout) || parseBoolean(gallery.dataset.showAll, false);

    items.forEach((item) => {
      const active = item === target;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-hidden', active || showAll ? 'false' : 'true');
      if (!showAll) item.hidden = !active;
    });

    let activeControl = null;
    controls.forEach((control, index) => {
      const controlTarget = findGalleryItem(items, galleryKeyForControl(control));
      const active = controlTarget ? controlTarget === target : index === targetIndex;
      control.classList.toggle('is-active', active);
      control.setAttribute('aria-current', active ? 'true' : 'false');
      if (control.getAttribute('role') === 'tab') control.setAttribute('aria-selected', active ? 'true' : 'false');
      control.tabIndex = active ? 0 : -1;
      if (active) activeControl = control;
    });

    if (focusControl && activeControl) activeControl.focus({ preventScroll: true });
    if (scrollMedia && gallery.dataset.galleryLayout === 'slider') {
      const stage = target.parentElement;
      if (stage && typeof stage.scrollTo === 'function') {
        stage.scrollTo({
          left: target.offsetLeft - stage.offsetLeft,
          behavior: isMotionReduced() ? 'auto' : 'smooth'
        });
      }
    }
    gallery.dispatchEvent(new CustomEvent('ww:gallery-change', {
      bubbles: true,
      detail: { media: target, index: targetIndex }
    }));
  }

  function stepGallery(control, direction) {
    const gallery = control.closest(GALLERY_SELECTOR);
    if (!gallery) return;
    const controls = galleryControls(gallery);
    if (!controls.length) return;
    let index = controls.indexOf(control);
    if (index < 0) index = controls.findIndex((item) => item.getAttribute('aria-current') === 'true');
    index = (index + direction + controls.length) % controls.length;
    activateGallery(gallery, controls[index], true, true);
  }

  function initGalleries(root) {
    elementsWithin(root, GALLERY_SELECTOR).forEach((gallery) => {
      if (gallery.dataset.wwGalleryReady === 'true') return;
      gallery.dataset.wwGalleryReady = 'true';
      const controls = galleryControls(gallery);
      const selected = controls.find((control) =>
        control.getAttribute('aria-current') === 'true' || control.getAttribute('aria-selected') === 'true'
      );
      activateGallery(gallery, selected || controls[0] || '0');
    });
  }

  function productRootFor(element) {
    return element.closest(PRODUCT_ROOT_SELECTOR) || element.closest('.shopify-section') || document;
  }

  function productVariants(root) {
    const script = root.querySelector('[data-product-variants], [data-variant-json], script[data-product-json]');
    if (!script) return [];
    if (script._wwVariants) return script._wwVariants;
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      script._wwVariants = Array.isArray(parsed) ? parsed : parsed.variants || [];
    } catch (_) {
      script._wwVariants = [];
    }
    return script._wwVariants;
  }

  function selectedOptions(root) {
    const values = [];
    const names = new Set();
    root.querySelectorAll('select[name^="options["], input[name^="options["]:checked').forEach((input) => {
      if (names.has(input.name)) return;
      names.add(input.name);
      values.push(input.value);
    });
    return values;
  }

  function formatMoney(cents, format) {
    if (cents == null || cents === '') return '';
    const value = typeof cents === 'string' ? cents.replace('.', '') : cents;
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';
    const template = format || theme.moneyFormat || '${{amount}}';
    const token = template.match(/\{\{\s*(\w+)\s*\}\}/);
    const placeholder = token ? token[1] : 'amount';
    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.length > 1 ? `${parts[0]}${decimal}${parts[1]}` : parts[0];
    };
    let output;
    if (placeholder === 'amount_no_decimals') output = withDelimiters(amount, 0, ',', '.');
    else if (placeholder === 'amount_with_comma_separator') output = withDelimiters(amount, 2, '.', ',');
    else if (placeholder === 'amount_no_decimals_with_comma_separator') output = withDelimiters(amount, 0, '.', ',');
    else if (placeholder === 'amount_with_apostrophe_separator') output = withDelimiters(amount, 2, "'", '.');
    else output = withDelimiters(amount, 2, ',', '.');
    return token ? template.replace(token[0], output) : output;
  }

  function setButtonLabel(button, label) {
    const labelNode = button.querySelector('[data-add-label]');
    if (labelNode) labelNode.textContent = label;
    else button.textContent = label;
  }

  function updateVariant(source) {
    const root = productRootFor(source);
    const variants = productVariants(root);
    let variantId = '';

    if (source.matches('[name="id"]')) variantId = source.value;
    if (!variantId) variantId = root.querySelector('[name="id"]')?.value || '';

    const optionValues = selectedOptions(root);
    let variant = variants.find((item) => String(item.id) === String(variantId));
    if (optionValues.length && variants.length) {
      variant = variants.find((item) => {
        const options = item.options || [item.option1, item.option2, item.option3].filter((value) => value != null);
        return optionValues.every((value, index) => String(options[index]) === String(value));
      }) || null;
    }

    if (variant) variantId = String(variant.id);
    else if (optionValues.length && variants.length) variantId = '';
    if (variantId) {
      root.querySelectorAll('[name="id"]').forEach((input) => {
        if (input.matches('input[type="radio"], input[type="checkbox"]')) {
          input.checked = String(input.value) === variantId;
        } else if (Array.from(input.options || []).some((option) => option.value === variantId) || input.tagName !== 'SELECT') {
          input.value = variantId;
        }
      });
    }

    const selected = source.selectedOptions ? source.selectedOptions[0] : null;
    const dataAvailable = selected?.dataset.available ?? source.dataset.available;
    const invalidCombination = optionValues.length && variants.length && !variant;
    const available = invalidCombination ? false : variant
      ? Boolean(variant.available)
      : dataAvailable == null
        ? Boolean(variantId)
        : parseBoolean(dataAvailable, true);
    const availabilityLabel = available
      ? root.dataset.availableText || 'Available'
      : variantId
        ? root.dataset.soldOutText || 'Sold out'
        : root.dataset.unavailableText || 'Product unavailable';

    root.querySelectorAll('[data-product-availability]').forEach((node) => {
      node.textContent = availabilityLabel;
    });

    root.querySelectorAll('form[action*="/cart/add"] [type="submit"][name="add"], [data-add-to-cart]').forEach((button) => {
      if (!button.dataset.wwAvailableLabel) {
        button.dataset.wwAvailableLabel = button.dataset.availableText || button.textContent.trim() || 'Add to cart';
      }
      const unavailableLabel = variantId
        ? button.dataset.soldOutText || root.dataset.soldOutText || 'Sold out'
        : button.dataset.unavailableText || root.dataset.unavailableText || 'Product unavailable';
      button.disabled = !available;
      button.setAttribute('aria-disabled', available ? 'false' : 'true');
      setButtonLabel(button, available ? button.dataset.wwAvailableLabel : unavailableLabel);
    });

    const formattedPrice = selected?.dataset.price || source.dataset.price ||
      (variant ? formatMoney(variant.price, theme.moneyFormat) : '');
    const formattedCompare = variant
      ? (Number(variant.compare_at_price) > Number(variant.price) ? formatMoney(variant.compare_at_price, theme.moneyFormat) : '')
      : (selected?.dataset.compareAt ?? source.dataset.compareAt ?? '');
    if (formattedPrice) root.querySelectorAll('[data-product-price], [data-sticky-price]').forEach((node) => {
      node.textContent = formattedPrice;
    });
    root.querySelectorAll('[data-compare-price]').forEach((node) => {
      node.textContent = formattedCompare;
      node.hidden = !formattedCompare;
    });

    const mediaId = selected?.dataset.mediaId || selected?.dataset.featuredMediaId || source.dataset.mediaId ||
      variant?.featured_media?.id || variant?.featured_image?.id;
    if (mediaId) activateGallery(root.querySelector(GALLERY_SELECTOR), String(mediaId), false, true);

    const updateUrl = root.dataset.updateUrl !== 'false' && document.body.classList.contains('template-product');
    if (variantId && updateUrl && window.history?.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState({}, '', url.toString());
    }

    root.dispatchEvent(new CustomEvent('ww:variant-change', {
      bubbles: true,
      detail: { variant, variantId, available }
    }));
  }

  function setStickyBarVisibility(bar, visible) {
    bar.classList.toggle('is-visible', visible);
    bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    if (visible) bar.removeAttribute('inert');
    else bar.setAttribute('inert', '');
  }

  function setupStickyAdd(root) {
    const bars = elementsWithin(root, '[data-sticky-add-bar], [data-sticky-bar], [data-sticky-add]:not(button):not(a):not(form)');
    bars.forEach((bar) => {
      if (bar.dataset.wwStickyReady === 'true') return;
      bar.dataset.wwStickyReady = 'true';
      const productRoot = productRootFor(bar);
      const formId = bar.dataset.formId;
      const form = (formId && document.getElementById(formId)) || productRoot.querySelector('form[action*="/cart/add"]');
      const target = form?.querySelector('[type="submit"][name="add"], [data-add-to-cart]');
      if (!target || !('IntersectionObserver' in window)) {
        setStickyBarVisibility(bar, false);
        return;
      }
      setStickyBarVisibility(bar, false);
      const observer = new IntersectionObserver(([entry]) => {
        setStickyBarVisibility(bar, !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }, { threshold: 0 });
      observer.observe(target);
      stickyRecords.add({ bar, observer });
    });
  }

  function initProducts(root) {
    elementsWithin(root, PRODUCT_ROOT_SELECTOR).forEach((productRoot) => {
      if (productRoot.dataset.wwProductReady === 'true') return;
      productRoot.dataset.wwProductReady = 'true';
      const source = productRoot.querySelector('[data-variant-select]:checked, input[name="id"]:checked') ||
        productRoot.querySelector('[data-variant-select], select[name="id"], input[name="id"], select[name^="options["]');
      if (source) updateVariant(source);
    });
    setupStickyAdd(root);
  }

  function jsonRoute(route) {
    const url = new URL(route || '/', window.location.origin);
    if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return `${url.pathname}${url.search}`;
  }

  async function responseJSON(response) {
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new Error(data.description || data.message || 'The request could not be completed.');
      error.status = response.status;
      throw error;
    }
    return data;
  }

  async function requestRenderedSections() {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', 'cart-drawer');
    const response = await fetch(url.toString(), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    });
    return responseJSON(response);
  }

  function replacementFromHTML(id, html) {
    if (typeof html !== 'string' || !html.trim()) return null;
    const template = document.createElement('template');
    template.innerHTML = html.trim();
    return template.content.querySelector(`#shopify-section-${id}`) || template.content.firstElementChild;
  }

  function replaceSection(id, html) {
    const target = document.getElementById(`shopify-section-${id}`);
    const replacement = replacementFromHTML(id, html);
    if (!target || !replacement) return false;
    target.replaceWith(replacement);
    return true;
  }

  function syncCartCountFromDrawer() {
    const shell = document.querySelector('[data-cart-shell]');
    if (!shell) return;
    const count = Number.parseInt(shell.dataset.cartRenderedCount || '', 10);
    if (!Number.isFinite(count)) return;
    document.querySelectorAll('[data-cart-count]').forEach((element) => {
      element.textContent = String(count);
    });
    document.querySelectorAll('[data-cart-open]').forEach((element) => {
      element.setAttribute('aria-label', `Cart (${count})`);
    });
  }

  function applyRenderedSections(sections) {
    if (!sections || typeof sections !== 'object') return false;
    const keepCartOpen = activeSurface?.kind === 'cart';
    const previousCartSurface = keepCartOpen ? activeSurface : null;
    const cartOpener = keepCartOpen ? activeSurface.opener : null;

    try {
      if (keepCartOpen && sections['cart-drawer']) activeSurface = null;

      const cartReplaced = replaceSection('cart-drawer', sections['cart-drawer']);
      init(document);
      syncCartCountFromDrawer();
      updateHeader();

      if (keepCartOpen && cartReplaced) {
        openSurface('cart', cartOpener && cartOpener.isConnected ? cartOpener : document.querySelector('[data-cart-open]'), {
          preserveLock: true
        });
      } else if (keepCartOpen && !cartReplaced) {
        activeSurface = previousCartSurface?.shell?.isConnected ? previousCartSurface : null;
      }
      return cartReplaced;
    } finally {
      if (keepCartOpen && activeSurface?.kind !== 'cart') unlockBody();
    }
  }

  async function ensureSections(data) {
    let sections = data && data.sections;
    if (!sections || !sections['cart-drawer']) {
      sections = await requestRenderedSections();
    }
    return applyRenderedSections(sections);
  }

  function productFeedback(form, message, isError = false) {
    let node = form.querySelector('[data-product-feedback]');
    if (!node) {
      node = document.createElement('p');
      node.dataset.productFeedback = '';
      form.appendChild(node);
    }
    node.className = 'ww-product-feedback' + (isError ? ' ww-product-feedback--error' : '');
    node.setAttribute('role', isError ? 'alert' : 'status');
    node.textContent = message;
    node.hidden = !message;
  }

  async function addProduct(form, submitter) {
    if (pendingForms.has(form)) return;
    pendingForms.add(form);
    productFeedback(form, '');
    form.setAttribute('aria-busy', 'true');
    const wasDisabled = submitter ? submitter.disabled : false;
    if (submitter) {
      submitter.disabled = true;
      submitter.classList.add('is-loading');
    }

    try {
      const formData = new FormData(form);
      formData.set('sections', 'cart-drawer');
      formData.set('sections_url', window.location.pathname);
      const response = await fetch(jsonRoute(form.action || theme.routes?.cartAdd || '/cart/add'), {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      });
      const data = await responseJSON(response);
      // An accepted cart mutation must not be reported as a failed add when only
      // the presentation refresh fails. Never retry the mutation automatically.
      let drawerReady = false;
      try { drawerReady = await ensureSections(data); } catch (_) {}

      const success = form.dataset.successMessage || submitter?.dataset.successMessage || 'Added to cart.';
      announce(success);
      productFeedback(form, success);
      document.dispatchEvent(new CustomEvent('ww:cart-updated', { detail: data }));

      const openedDrawer = drawerReady && theme.cartPresentation === 'drawer' && openSurface('cart', submitter);
      if (!openedDrawer) window.location.assign(theme.routes?.cart || '/cart');
    } catch (error) {
      const message = error.message || 'The item could not be added to your cart. Please try again.';
      productFeedback(form, message, true);
      announce(message);
      form.dispatchEvent(new CustomEvent('ww:cart-error', { bubbles: true, detail: { error } }));
    } finally {
      pendingForms.delete(form);
      form.removeAttribute('aria-busy');
      if (submitter && submitter.isConnected) {
        submitter.disabled = wasDisabled;
        submitter.classList.remove('is-loading');
      }
    }
  }

  async function changeCart(line, quantity, trigger) {
    if (cartRequestPending || !Number.isFinite(line) || !Number.isFinite(quantity)) return;
    cartRequestPending = true;
    const controls = Array.from(trigger?.closest('[data-cart-line]')?.querySelectorAll('[data-cart-change]') || []);
    const controlIndex = controls.indexOf(trigger);
    trigger?.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(jsonRoute(theme.routes?.cartChange || '/cart/change'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          line,
          quantity,
          sections: ['cart-drawer'],
          sections_url: window.location.pathname
        })
      });
      const data = await responseJSON(response);
      let drawerReady = false;
      try { drawerReady = await ensureSections(data); } catch (_) {}
      if (!drawerReady) { window.location.assign(theme.routes?.cart || '/cart'); return; }
      if (quantity > 0 && controlIndex >= 0) {
        const next = document.querySelector(`[data-cart-line="${line}"]`)?.querySelectorAll('[data-cart-change]')[controlIndex];
        window.requestAnimationFrame(() => next?.focus({ preventScroll: true }));
      }
      announce(quantity === 0 ? 'Item removed from cart.' : 'Cart updated.');
      document.dispatchEvent(new CustomEvent('ww:cart-updated', { detail: data }));
    } catch (error) {
      announce(error.message || 'The cart could not be updated.');
    } finally {
      cartRequestPending = false;
      if (trigger?.isConnected) trigger.removeAttribute('aria-busy');
    }
  }

  function cartChangeFromLink(link) {
    try {
      const url = new URL(link.href, window.location.origin);
      return {
        line: Number.parseInt(url.searchParams.get('line'), 10),
        quantity: Number.parseInt(url.searchParams.get('quantity') || '0', 10)
      };
    } catch (_) {
      return { line: NaN, quantity: NaN };
    }
  }

  function initAddresses(root) {
    elementsWithin(root, '[data-address-country]').forEach((country) => {
      if (country.dataset.wwAddressReady) return;
      country.dataset.wwAddressReady = 'true';
      const form = country.closest('form');
      const container = form?.querySelector('[data-address-province]');
      const select = container?.querySelector('[data-province-select]');
      const input = container?.querySelector('[data-province-input]');
      if (!select || !input) return;
      const selectedCountry = Array.from(country.options).find(o => o.value === country.dataset.default || o.textContent === country.dataset.default);
      if (selectedCountry) country.value = selectedCountry.value;
      const populate = (initial) => {
        let provinces = [];
        try { provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) {}
        if (!Array.isArray(provinces)) provinces = [];
        select.textContent = '';
        for (const item of provinces) {
          const option = new Option(item[1] || item[0], item[0]);
          select.add(option);
        }
        const preferred = initial ? select.dataset.default : '';
        const option = Array.from(select.options).find(o => o.value === preferred || o.textContent === preferred);
        if (option) select.value = option.value;
        container.hidden = provinces.length === 0;
        input.hidden = true;
        input.disabled = true;
        select.hidden = provinces.length === 0;
        select.disabled = provinces.length === 0;
        // Transfer the visible label to the enhanced control, keeping a usable
        // free-text province field when JavaScript is unavailable.
        if (select.id === '') { select.id = input.id + '-select'; container.querySelector('label')?.setAttribute('for', select.id); }
      };
      country.addEventListener('change', () => populate(false));
      populate(true);
    });
  }

  function init(root = document) {
    initFooterDisclosures(root);
    syncFilterDrawers(root);
    initDisclosures(root);
    initMotionControls(root);
    initIntros(root);
    initReveals(root);
    initCategoryRails(root);
    initGalleries(root);
    initProducts(root);
    initAddresses(root);
    scheduleHeaderUpdate();
  }

  function onClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const motionToggle = target.closest('[data-motion-toggle]');
    if (motionToggle) {
      event.preventDefault();
      const control = motionToggle.closest('[data-motion-control]');
      const menu = control?.querySelector('[data-motion-menu]');
      if (!menu) return;
      const willOpen = menu.hidden;
      closeMotionMenus(control);
      menu.hidden = !willOpen;
      motionToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      if (willOpen) menu.querySelector('[aria-pressed="true"]')?.focus();
      return;
    }
    const motionValue = target.closest('[data-motion-value]');
    if (motionValue) {
      event.preventDefault();
      const motionToggle = motionValue.closest('[data-motion-control]')?.querySelector('[data-motion-toggle]');
      applyMotionPreference(motionValue.dataset.motionValue);
      closeMotionMenus();
      motionToggle?.focus({ preventScroll: true });
      return;
    }
    if (!target.closest('[data-motion-control]')) closeMotionMenus();

    const categoryDot = target.closest('[data-category-dot]');
    if (categoryDot) {
      event.preventDefault();
      const shell = categoryDot.closest('[data-category-shell]');
      const rail = shell?.querySelector('[data-category-rail]');
      const cards = rail ? Array.from(rail.querySelectorAll('[data-category-card]')) : [];
      const card = cards[Number.parseInt(categoryDot.dataset.categoryDot, 10)];
      if (rail && card) rail.scrollTo({ left: card.offsetLeft, behavior: isMotionReduced() ? 'auto' : 'smooth' });
      return;
    }

    const menuOpen = target.closest('[data-menu-open]');
    if (menuOpen) {
      event.preventDefault();
      openSurface('menu', menuOpen);
      return;
    }
    const cartOpen = target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      if (!openSurface('cart', cartOpen)) window.location.assign(cartOpen.href || theme.routes?.cart || '/cart');
      return;
    }
    const filterOpen = target.closest('[data-filter-open], [data-filter-toggle]');
    if (filterOpen) {
      event.preventDefault();
      openSurface('filter', filterOpen);
      return;
    }

    if (target.closest('[data-menu-close]')) {
      event.preventDefault();
      if (activeSurface?.kind === 'menu') closeSurface();
      return;
    }
    if (target.closest('[data-cart-close]')) {
      event.preventDefault();
      if (activeSurface?.kind === 'cart') closeSurface();
      return;
    }
    if (target.closest('[data-filter-close], [data-filter-apply]')) {
      if (target.closest('[data-filter-close]')) event.preventDefault();
      if (activeSurface?.kind === 'filter') closeSurface();
      return;
    }

    if (activeSurface?.kind === 'menu' && activeSurface.shell.contains(target) && target.closest('a[href]')) {
      closeSurface({ returnFocus: false });
      return;
    }

    const footerToggle = target.closest('[data-footer-toggle]');
    if (footerToggle) {
      event.preventDefault();
      if (!footerBreakpoint.matches) return;
      footerToggle.dataset.mobileExpanded = footerToggle.dataset.mobileExpanded === 'true' ? 'false' : 'true';
      syncFooterDisclosure(footerToggle);
      return;
    }

    const disclosureButton = target.closest('[data-disclosure-button]');
    if (disclosureButton && !disclosureButton.closest('details')) {
      event.preventDefault();
      toggleCustomDisclosure(disclosureButton);
      return;
    }

    const galleryControl = target.closest(GALLERY_CONTROL_SELECTOR);
    if (galleryControl) {
      const gallery = galleryControl.closest(GALLERY_SELECTOR);
      if (gallery) {
        event.preventDefault();
        activateGallery(gallery, galleryControl, false, true);
      }
      return;
    }
    const galleryNext = target.closest('[data-gallery-next], [data-gallery-previous]');
    if (galleryNext) {
      event.preventDefault();
      const gallery = galleryNext.closest(GALLERY_SELECTOR);
      const controls = gallery ? galleryControls(gallery) : [];
      const active = controls.find((control) => control.getAttribute('aria-current') === 'true') || controls[0];
      if (active) stepGallery(active, galleryNext.matches('[data-gallery-next]') ? 1 : -1);
      return;
    }

    const stickyButton = target.closest('[data-sticky-add-button], button[data-sticky-add]');
    if (stickyButton && !stickyButton.closest('form[action*="/cart/add"]')) {
      event.preventDefault();
      const root = productRootFor(stickyButton);
      const formId = stickyButton.dataset.formId || stickyButton.closest('[data-form-id]')?.dataset.formId;
      const form = (formId && document.getElementById(formId)) || root.querySelector('form[action*="/cart/add"]');
      if (form) form.requestSubmit(form.querySelector('[type="submit"][name="add"]') || undefined);
      return;
    }

    const cartChange = target.closest('[data-cart-change]');
    if (cartChange) {
      event.preventDefault();
      changeCart(
        Number.parseInt(cartChange.dataset.cartChange, 10),
        Number.parseInt(cartChange.dataset.quantity, 10),
        cartChange
      );
      return;
    }

    const removeLink = target.closest('a[href*="/cart/change"]');
    if (removeLink && removeLink.closest('[data-cart-drawer], [data-cart-shell]')) {
      const change = cartChangeFromLink(removeLink);
      if (Number.isFinite(change.line) && Number.isFinite(change.quantity)) {
        event.preventDefault();
        changeCart(change.line, change.quantity, removeLink);
      }
      return;
    }

    if (activeIntro && (target.closest('[data-intro-skip]') || activeIntro.contains(target))) {
      event.preventDefault();
      finishIntro(activeIntro, true);
    }
  }

  function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-address-delete-confirm') && !window.confirm(form.dataset.addressDeleteConfirm)) { event.preventDefault(); return; }
    const action = form.getAttribute('action') || '';
    const isProductForm = form.matches('[data-product-form], [data-ajax-cart-form]') || /\/cart\/add(?:\.js)?(?:\?|$)/.test(action);
    if (!isProductForm || form.dataset.ajax === 'false' || !window.fetch || event.defaultPrevented) return;
    if (event.submitter?.name === 'checkout' || event.submitter?.hasAttribute('data-no-ajax')) return;
    event.preventDefault();
    addProduct(form, event.submitter || form.querySelector('[type="submit"]'));
  }

  function onChange(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.matches('[data-variant-select], select[name="id"], input[name="id"], select[name^="options["], input[name^="options["]')) {
      updateVariant(target);
    }
    if (target.matches('[data-cart-quantity]')) {
      changeCart(
        Number.parseInt(target.dataset.line || target.dataset.cartLine, 10),
        Number.parseInt(target.value, 10),
        target
      );
    }
    if (target.matches('[data-filter-auto-submit], [data-sort-auto-submit]')) target.form?.requestSubmit();
  }

  function onKeydown(event) {
    if (event.key === 'Escape' && document.querySelector('[data-motion-toggle][aria-expanded="true"]')) {
      const trigger = document.querySelector('[data-motion-toggle][aria-expanded="true"]');
      closeMotionMenus();
      trigger?.focus();
      return;
    }
    if (activeSurface) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSurface();
        return;
      }
      trapFocus(event);
    }

    const control = event.target instanceof Element ? event.target.closest(GALLERY_CONTROL_SELECTOR) : null;
    if (control && ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      const controls = galleryControls(control.closest(GALLERY_SELECTOR));
      if (event.key === 'Home') activateGallery(control.closest(GALLERY_SELECTOR), controls[0], true);
      else if (event.key === 'End') activateGallery(control.closest(GALLERY_SELECTOR), controls[controls.length - 1], true);
      else stepGallery(control, ['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1);
      return;
    }

    if (activeIntro) finishIntro(activeIntro, true);
  }

  function onFocusIn(event) {
    if (activeSurface && !activeSurface.shell.contains(event.target)) {
      const target = focusableWithin(activeSurface.dialog)[0] || activeSurface.dialog;
      target.focus({ preventScroll: true });
    }
    if (event.target instanceof Element && event.target.closest('[data-ww-header]')) {
      document.querySelector('[data-ww-header]')?.classList.remove('is-hidden');
    }
  }

  function onDetailsToggle(event) {
    if (event.target instanceof HTMLDetailsElement && event.target.matches('[data-disclosure]')) {
      syncNativeDisclosure(event.target);
    }
  }

  function cleanupSection(section) {
    if (activeSurface && section.contains(activeSurface.shell)) closeSurface({ returnFocus: false, immediate: true });
    elementsWithin(section, '[data-ww-intro]').forEach((intro) => {
      if (activeIntro === intro) {
        finishIntro(intro, true);
        return;
      }
      const timer = introTimers.get(intro);
      if (timer) window.clearTimeout(timer);
      introTimers.delete(intro);
      const stageTimers = introStageTimers.get(intro) || [];
      stageTimers.forEach((stageTimer) => window.clearTimeout(stageTimer));
      introStageTimers.delete(intro);
      intro.hidden = true;
    });
    elementsWithin(section, REVEAL_SELECTOR).forEach((element) => revealObserver?.unobserve(element));
    stickyRecords.forEach((record) => {
      if (section.contains(record.bar)) {
        record.observer.disconnect();
        stickyRecords.delete(record);
      }
    });
  }

  function boot() {
    if (booted) {
      safeInit(document);
      return;
    }
    booted = true;

    applyMotionPreference(currentMotionLevel(), false);

    document.addEventListener('click', onClick);
    document.addEventListener('submit', onSubmit);
    document.addEventListener('change', onChange);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('toggle', onDetailsToggle, true);
    window.addEventListener('scroll', scheduleHeaderUpdate, { passive: true });
    window.addEventListener('resize', scheduleHeaderUpdate, { passive: true });
    window.addEventListener('wheel', () => {
      if (activeIntro) finishIntro(activeIntro, true);
    }, { passive: true });
    window.addEventListener('touchmove', () => {
      if (activeIntro) finishIntro(activeIntro, true);
    }, { passive: true });

    document.addEventListener('shopify:section:load', (event) => safeInit(event.target));
    document.addEventListener('shopify:section:unload', (event) => cleanupSection(event.target));
    document.addEventListener('shopify:block:select', (event) => {
      const disclosure = event.target.closest?.('[data-disclosure], .ww-footer__group');
      if (disclosure?.tagName === 'DETAILS') disclosure.open = true;
      const footerToggle = disclosure?.querySelector?.('[data-footer-toggle]');
      if (footerToggle) {
        footerToggle.dataset.mobileExpanded = 'true';
        syncFooterDisclosure(footerToggle);
      }
    });

    const syncMotion = () => {
      if (isMotionReduced()) {
        if (activeIntro) finishIntro(activeIntro, true);
        document.querySelectorAll(REVEAL_SELECTOR).forEach(revealElement);
      }
    };
    if (reducedMotion.addEventListener) reducedMotion.addEventListener('change', syncMotion);
    else if (reducedMotion.addListener) reducedMotion.addListener(syncMotion);
    const syncResponsiveDrawers = () => {
      syncAllFooterDisclosures();
      syncFilterDrawers(document);
    };
    if (footerBreakpoint.addEventListener) footerBreakpoint.addEventListener('change', syncResponsiveDrawers);
    else if (footerBreakpoint.addListener) footerBreakpoint.addListener(syncResponsiveDrawers);

    document.documentElement.classList.remove('no-js');
    document.documentElement.classList.add('js');
    safeInit(document);
  }

  theme.runtime = {
    version: VERSION,
    init: safeInit,
    announce,
    openCart(opener) { return openSurface('cart', opener); },
    closeDrawer() { closeSurface(); },
    async refreshCart() {
      const sections = await requestRenderedSections();
      applyRenderedSections(sections);
      return sections;
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
